import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Check } from 'lucide-react';
import { supabase } from '../services/backendService';

export const PartnersPage: React.FC = () => {
    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        organization: '',
        inquiryType: 'Health Institute or Wellness Center',
        message: '',
    });

    const [isSending, setIsSending] = React.useState(false);
    const [isSent, setIsSent] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSending(true);
        setError(null);

        try {
            const { error: funcError } = await supabase.functions.invoke('send-contact-email', {
                body: {
                    source: 'partners',
                    name: formData.name,
                    email: formData.email,
                    organization: formData.organization,
                    category: formData.inquiryType,
                    message: formData.message
                }
            });

            if (funcError) throw funcError;

            setIsSent(true);
            setFormData({
                name: '',
                email: '',
                organization: '',
                inquiryType: 'Health Institute or Wellness Center',
                message: ''
            });
        } catch (err: any) {
            console.error('Error sending partnership inquiry:', err);
            setError('Failed to send inquiry. Please try again or email us at hello@naturenani.com.');
        } finally {
            setIsSending(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-12">
            {/* 1. Partners Page Hero */}
            <div className="text-center mb-16">
                <div className="mb-3 text-sage-600 font-bold tracking-wider uppercase text-sm">Work With Us</div>
                <h1 className="text-4xl md:text-5xl font-bold text-sage-900 mb-6">Bring Ancient Wisdom to Your Community</h1>
                <p className="text-xl text-sage-700 max-w-3xl mx-auto leading-relaxed">
                    NatureNani partners with health institutes, wellness practitioners, educators, and mission-aligned organisations to make Ayurvedic and naturopathic wisdom more accessible — and more actionable — for the people you serve.
                </p>
            </div>

            <div className="space-y-16">
                {/* 2. Four Partnership Sections */}
                <section>
                    <h2 className="text-3xl font-bold text-sage-900 mb-8 border-b border-sage-200 pb-2">How We Can Work Together</h2>
                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Card 1 */}
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-sage-100 flex flex-col items-start gap-4">
                            <span className="text-4xl">🏥</span>
                            <h3 className="font-bold text-sage-900 text-2xl mb-2">Health Institutes & Wellness Centers</h3>
                            <p className="text-sage-700 text-sm leading-relaxed">
                                Looking for a verified educational resource to support your clients' wellness literacy? NatureNani can serve as a supplemental learning tool — helping your clients understand their Ayurvedic constitution, explore root-cause thinking, and become more informed participants in their own health.
                            </p>
                            <div className="w-full mt-4 flex-grow">
                                <h4 className="font-bold text-sage-900 text-sm mb-3">What we offer:</h4>
                                <ul className="list-disc pl-5 space-y-2 text-sm text-sage-700 font-medium">
                                    <li>Educational content grounded in 200+ verified ancient Ayurvedic texts</li>
                                    <li>Constitution-based (Dosha) personalisation for individual clients</li>
                                    <li>A "both/and" bridge between natural wisdom and conventional care</li>
                                </ul>
                            </div>
                            <div className="w-full mt-6 pt-4 border-t border-sage-100">
                                <p className="text-xs text-sage-500 italic">NatureNani is an educational platform. We complement professional care — we do not provide medical advice, diagnosis, or treatment.</p>
                            </div>
                        </div>

                        {/* Card 2 */}
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-sage-100 flex flex-col items-start gap-4">
                            <span className="text-4xl">🧘</span>
                            <h3 className="font-bold text-sage-900 text-2xl mb-2">Wellness Practitioners & Yoga Studios</h3>
                            <p className="text-sage-700 text-sm leading-relaxed">
                                Help your clients go deeper. NatureNani gives your students and clients an at-home Ayurvedic companion — grounded in classical texts, personalised to their constitution, and designed to extend and reinforce the work you do with them in person.
                            </p>
                            <div className="w-full mt-4 flex-grow">
                                <h4 className="font-bold text-sage-900 text-sm mb-3">Ideal for:</h4>
                                <ul className="list-disc pl-5 space-y-2 text-sm text-sage-700 font-medium">
                                    <li>Yoga instructors deepening students' Ayurvedic understanding</li>
                                    <li>Naturopathic practitioners seeking client educational support</li>
                                    <li>Wellness coaches incorporating traditional wisdom into modern practice</li>
                                    <li>Ayurvedic consultants wanting a digital tool to complement their sessions</li>
                                </ul>
                            </div>
                        </div>

                        {/* Card 3 */}
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-sage-100 flex flex-col items-start gap-4">
                            <span className="text-4xl">🗣️</span>
                            <h3 className="font-bold text-sage-900 text-2xl mb-2">Educators & Community Leaders</h3>
                            <p className="text-sage-700 text-sm leading-relaxed">
                                Passionate about natural health education? We provide the platform — you provide the community. Together we can bring verified Ayurvedic and naturopathic wisdom to audiences who have never had access to it.
                            </p>
                            <div className="w-full mt-4 flex-grow">
                                <h4 className="font-bold text-sage-900 text-sm mb-3">Opportunities:</h4>
                                <ul className="list-disc pl-5 space-y-2 text-sm text-sage-700 font-medium">
                                    <li>Educational content partnerships and co-creation</li>
                                    <li>Community wellness programs and workshops</li>
                                    <li>Learning events and live educational sessions</li>
                                </ul>
                            </div>
                        </div>

                        {/* Card 4 */}
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-sage-100 flex flex-col items-start gap-4">
                            <span className="text-4xl">🧩</span>
                            <h3 className="font-bold text-sage-900 text-2xl mb-2">Strategic Partnerships</h3>
                            <p className="text-sage-700 text-sm leading-relaxed">
                                Have an idea that makes ancient healing traditions more accessible? We're open to collaborations that align with our mission — particularly where technology, research, or distribution can extend NatureNani's reach to communities that traditional wellness has historically underserved.
                            </p>
                            <div className="w-full mt-4 flex-grow">
                                <h4 className="font-bold text-sage-900 text-sm mb-3">Areas of interest:</h4>
                                <ul className="list-disc pl-5 space-y-2 text-sm text-sage-700 font-medium">
                                    <li>Educational content development and curriculum design</li>
                                    <li>Technology integrations that enhance personalised wellness guidance</li>
                                    <li>Research collaborations on traditional medicine education</li>
                                    <li>Distribution partnerships aligned with the NatureNani mission</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 3. Partnership Form */}
                <section className="bg-white p-8 md:p-12 rounded-2xl shadow-lg border border-sage-100 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-2 h-full bg-sage-500"></div>
                    <div className="max-w-3xl mx-auto">
                        <div className="mb-10 text-center">
                            <h2 className="text-3xl font-bold text-sage-900 mb-4">Start a Conversation</h2>
                            <p className="text-lg text-sage-600">
                                Navilla personally reviews every partnership inquiry. Tell us about your vision — we'll respond within 5–7 business days if there's mutual alignment.
                            </p>
                        </div>

                        <form className="space-y-6" onSubmit={handleSubmit}>
                            {isSent ? (
                                <div className="bg-sage-50 border border-sage-200 p-10 rounded-2xl text-center animate-in fade-in zoom-in duration-300">
                                    <div className="w-16 h-16 bg-sage-100 text-sage-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Check size={32} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-sage-900 mb-2">Inquiry Received!</h3>
                                    <p className="text-sage-600 mb-6">Namaste! We've received your partnership vision and will get back to you within 5–7 business days.</p>
                                    <button
                                        type="button"
                                        onClick={() => setIsSent(false)}
                                        className="text-sage-600 font-bold hover:text-sage-800 underline underline-offset-4"
                                    >
                                        Send another inquiry
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-sage-700 mb-1">Name <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-sage-200 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none transition-all bg-sage-50/50"
                                                placeholder="Your name"
                                                required
                                                disabled={isSending}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-sage-700 mb-1">Email <span className="text-red-500">*</span></label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-sage-200 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none transition-all bg-sage-50/50"
                                                placeholder="you@example.com"
                                                required
                                                disabled={isSending}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-sage-700 mb-1">Organisation</label>
                                            <input
                                                type="text"
                                                name="organization"
                                                value={formData.organization}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-sage-200 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none transition-all bg-sage-50/50"
                                                placeholder="Your organisation (optional)"
                                                disabled={isSending}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-sage-700 mb-1">Partnership Type <span className="text-red-500">*</span></label>
                                            <select
                                                name="inquiryType"
                                                value={formData.inquiryType}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-sage-200 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none bg-sage-50/50 appearance-none"
                                                required
                                                disabled={isSending}
                                            >
                                                <option value="Health Institute or Wellness Center">Health Institute or Wellness Center</option>
                                                <option value="Wellness Practitioner or Yoga Studio">Wellness Practitioner or Yoga Studio</option>
                                                <option value="Educator or Community Leader">Educator or Community Leader</option>
                                                <option value="Strategic or Technology Partnership">Strategic or Technology Partnership</option>
                                                <option value="Something Else">Something Else</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-sage-700 mb-1">Tell us your vision <span className="text-red-500">*</span></label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-sage-200 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none h-32 resize-y transition-all bg-sage-50/50"
                                            placeholder="Describe your collaboration idea..."
                                            required
                                            disabled={isSending}
                                        ></textarea>
                                    </div>

                                    {error && (
                                        <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm border border-red-100">
                                            {error}
                                        </div>
                                    )}

                                    <div className="pt-4 text-center">
                                        <button
                                            type="submit"
                                            disabled={isSending}
                                            className="w-full sm:w-auto px-10 bg-sage-800 text-white py-4 rounded-xl font-bold text-lg hover:bg-sage-900 shadow-md transform active:scale-95 transition-all inline-flex justify-center items-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {isSending ? (
                                                <>Sending... <Loader2 size={20} className="animate-spin" /></>
                                            ) : (
                                                <>Send Inquiry <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" /></>
                                            )}
                                        </button>
                                        <p className="text-xs text-sage-500 mt-6 max-w-lg mx-auto">
                                            By submitting, you agree to our <Link to="/privacy" className="underline hover:text-sage-700">Privacy Policy</Link>. No partnership or confidential relationship is established by this inquiry.
                                        </p>
                                    </div>
                                </>
                            )}
                        </form>
                    </div>
                </section>

                {/* 4. Legal Note (Partners Page) */}
                <div className="border-t border-sage-100 pt-8 mt-16 text-center text-xs text-sage-400">
                    <p className="mb-4 max-w-2xl mx-auto leading-relaxed">
                        NatureNani is an educational technology platform. We are not a healthcare provider or covered entity under HIPAA. All content is for educational purposes only. See our full Disclaimer for complete terms.
                    </p>
                    <div className="flex justify-center flex-wrap gap-4">
                        <a href="mailto:hello@naturenani.com" className="hover:text-sage-600 transition-colors">hello@naturenani.com</a> ·
                        <a href="mailto:legal@naturenani.com" className="hover:text-sage-600 transition-colors">legal@naturenani.com</a> ·
                        <a href="mailto:press@naturenani.com" className="hover:text-sage-600 transition-colors">press@naturenani.com</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PartnersPage;
